import React, { createContext, useContext, useState, useEffect } from 'react';
import { syncCartActivity, syncSavedItems } from '../services/api';

const CartContext = createContext();
const EMPTY_ADDRESS = { street: '', city: '', state: '', zip: '', country: 'US' };
const EMPTY_PARCEL = { length: '', width: '', height: '', weightLb: '', weightOz: '' };
const CART_SESSION_KEY = 'anoraCartSessionId';
const SAVED_ITEMS_KEY = 'anoraSavedItems';

function getOrCreateCartSessionId() {
  try {
    const existing = localStorage.getItem(CART_SESSION_KEY);
    if (existing) return existing;

    const created = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `cart_${Date.now()}_${Math.random().toString(36).slice(2)}`;

    localStorage.setItem(CART_SESSION_KEY, created);
    return created;
  } catch {
    return `cart_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  }
}

function normalizeSavedParcel(parcel) {
  if (!parcel) return EMPTY_PARCEL;

  if ('weightLb' in parcel || 'weightOz' in parcel) {
    return { ...EMPTY_PARCEL, ...parcel };
  }

  const totalWeightOz = Number(parcel.weight);
  if (!Number.isFinite(totalWeightOz) || totalWeightOz <= 0) {
    return { ...EMPTY_PARCEL, ...parcel };
  }

  const pounds = Math.floor(totalWeightOz / 16);
  const ounces = totalWeightOz - (pounds * 16);

  return {
    ...EMPTY_PARCEL,
    ...parcel,
    weightLb: pounds ? String(pounds) : '',
    weightOz: ounces ? String(ounces) : '',
  };
}

function getCartSignature(items = []) {
  return [...items]
    .map((item) => `${item.id}:${item.qty}`)
    .sort()
    .join('|');
}

export function CartProvider({ children }) {
  const [cartSessionId] = useState(() => getOrCreateCartSessionId());
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('cart') || '[]');
    } catch { return []; }
  });
  const [savedItems, setSavedItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(SAVED_ITEMS_KEY) || '[]');
    } catch { return []; }
  });
  const [shippingQuote, setShippingQuote] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('cartShippingQuote') || 'null');
      return saved
        ? { ...saved, parcel: normalizeSavedParcel(saved.parcel) }
        : null;
    } catch {
      return null;
    }
  });
  const cartSignature = getCartSignature(items);
  const shippingQuoteStale = Boolean(
    shippingQuote?.cartSignature && shippingQuote.cartSignature !== cartSignature
  );
  const effectiveShippingQuote = shippingQuote
    ? { ...shippingQuote, stale: shippingQuoteStale }
    : null;

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    localStorage.setItem(SAVED_ITEMS_KEY, JSON.stringify(savedItems));
  }, [savedItems]);

  useEffect(() => {
    if (!cartSessionId) return;

    syncCartActivity({
      sessionId: cartSessionId,
      items: items.map((item) => ({ id: item.id, qty: item.qty })),
    }).catch(() => {});
  }, [cartSessionId, items]);

  useEffect(() => {
    if (!cartSessionId) return;

    syncSavedItems({
      sessionId: cartSessionId,
      items: savedItems.map((item) => ({ id: item.id })),
    }).catch(() => {});
  }, [cartSessionId, savedItems]);

  useEffect(() => {
    if (!items.length) {
      setShippingQuote(null);
      localStorage.removeItem('cartShippingQuote');
      return;
    }

    if (shippingQuote) {
      localStorage.setItem('cartShippingQuote', JSON.stringify(shippingQuote));
    } else {
      localStorage.removeItem('cartShippingQuote');
    }
  }, [items, shippingQuote]);

  const addItem = (product, qty = 1) => {
    setItems(prev => {
      const existing = prev.find(i => i.id === product.id);
      if (existing) {
        return prev.map(i =>
          i.id === product.id
            ? { ...i, qty: Math.min(i.qty + qty, product.quantity) }
            : i
        );
      }
      return [...prev, {
        id: product.id,
        title: product.title,
        price: product.price,
        image: product.images?.[0] || null,
        qty,
        maxQty: product.quantity,
      }];
    });
  };

  const removeItem = (id) => setItems(prev => prev.filter(i => i.id !== id));
  const removeSavedItem = (id) => setSavedItems(prev => prev.filter(i => i.id !== id));

  const updateQty = (id, qty) => {
    if (qty < 1) return removeItem(id);
    setItems(prev => prev.map(i => (
      i.id === id ? { ...i, qty: Math.min(qty, i.maxQty || qty) } : i
    )));
  };

  const addSavedItem = (product) => {
    setSavedItems((prev) => {
      if (prev.some((item) => item.id === product.id)) return prev;
      return [...prev, {
        id: product.id,
        title: product.title,
        price: product.price,
        base_price: product.base_price || product.price,
        on_sale: product.on_sale || false,
        sale_percent: product.sale_percent || 0,
        image: product.images?.[0] || product.image || null,
        maxQty: product.quantity,
      }];
    });
  };

  const moveToSaved = (id) => {
    const cartItem = items.find((item) => item.id === id);
    if (!cartItem) return;
    addSavedItem(cartItem);
    removeItem(id);
  };

  const moveSavedToCart = (id) => {
    const savedItem = savedItems.find((item) => item.id === id);
    if (!savedItem) return;
    addItem({
      id: savedItem.id,
      title: savedItem.title,
      price: savedItem.price,
      base_price: savedItem.base_price,
      on_sale: savedItem.on_sale,
      sale_percent: savedItem.sale_percent,
      quantity: savedItem.maxQty,
      images: savedItem.image ? [savedItem.image] : [],
      image: savedItem.image,
    }, 1);
    removeSavedItem(id);
  };

  const clearCart = () => setItems([]);
  const saveShippingQuote = (quote) => setShippingQuote({
    address: { ...EMPTY_ADDRESS, ...(quote?.address || {}) },
    parcel: normalizeSavedParcel(quote?.parcel),
    rates: quote?.rates || [],
    selectedRate: quote?.selectedRate || null,
    taxAmount: Number(quote?.taxAmount || 0),
    taxRate: Number(quote?.taxRate || 0),
    total: Number(quote?.total || 0),
    totalsPreviewed: Boolean(quote?.totalsPreviewed),
    cartSignature,
    updatedAt: new Date().toISOString(),
  });
  const clearShippingQuote = () => setShippingQuote(null);

  const subtotal = items.reduce((s, i) => s + i.price * i.qty, 0);
  const count = items.reduce((s, i) => s + i.qty, 0);

  return (
    <CartContext.Provider value={{
      items,
      savedItems,
      addItem,
      addSavedItem,
      removeItem,
      removeSavedItem,
      updateQty,
      moveToSaved,
      moveSavedToCart,
      clearCart,
      subtotal,
      count,
      shippingQuote: effectiveShippingQuote,
      saveShippingQuote,
      clearShippingQuote,
      cartSignature,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
